// App — アプリシェル (設計書 §5.1)。4 画面 = title / play / studio / settings。
//   - play (SoloMahjong) は一度 mount したら **display:none で隠すだけ**（対局状態保持が必須要件。
//     旧タブ切替の unmount で対局が消えていた問題の解消）。
//   - 工房 (studio) = 既存 6 パネル (RuleSet/Yaku/AddYaku/ScoreTable/ExampleHand/PlaySandbox) を
//     画面内サブタブで表示（従来どおり遅延 mount/unmount で可）。Save config は工房ヘッダ右端のみ。
//   - ナビ: <768px は画面下タブバー（対局/工房/設定。play・title では非表示 — play は HUD の ☰、
//     title は画面内リンクが担う）、≥768px は上部スリムバー 48px（左ロゴ「雀」→タイトルへ）。
//   - ルートに data-motion="full|reduced" (D15)。boot 時 hydrate (loadConfig→store) は現行どおり。
// integrate 連携 seam（SoloMahjong に props を増やさない規約のため window CustomEvent を使う）:
//   - 受信 'mj:navigate'           detail: 'title'|'play'|'studio'|'settings' — HUD ☰ メニュー等から画面遷移
//   - 送信 'mj:open-game-settings' タイトルの「人数・ルールを選んで開始」→ 対局設定ドロワー自動オープン
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useStudioStore, useStudioApi } from './useStore';
import { RuleSetEditor } from './panels/RuleSetEditor';
import { YakuToggle } from './panels/YakuToggle';
import { ScoreTableInspector } from './panels/ScoreTableInspector';
import { ExampleHand } from './panels/ExampleHand';
import { PlaySandbox } from './panels/PlaySandbox';
import { SoloMahjong } from './panels/SoloMahjong';
import { OrientationGate } from './render/OrientationGate';
import { AddYaku } from './panels/AddYaku';
import { useConfigSaveListener, saveRuleSet, saveCatalog, loadConfig } from './saveClient';
import { HomeScreen } from './ui/HomeScreen';
import { OnlineLobby } from './ui/OnlineLobby';
import { SettingsPanel } from './ui/SettingsPanel';
import { getSettings, useEffectiveReduced } from './ui/settings';
import { sfx } from './ui/sound';
import { exitOnline, getOnlineTable, useOnlinePhase, type ExitTo } from './net/onlineSession';

type Screen = 'title' | 'play' | 'online' | 'studio' | 'settings';

type StudioTab = 'rules' | 'yaku' | 'addYaku' | 'scoreTable' | 'example' | 'play';
const STUDIO_TABS: { id: StudioTab; label: string }[] = [
  { id: 'rules', label: 'RuleSet' },
  { id: 'yaku', label: 'Yaku' },
  { id: 'addYaku', label: 'Add Yaku' },
  { id: 'scoreTable', label: 'Score Table' },
  { id: 'example', label: 'Example Hand' },
  { id: 'play', label: 'Play Sandbox' },
];

const NAV: { id: Screen; label: string; icon: string }[] = [
  { id: 'play', label: '対局', icon: '🀄' },
  { id: 'studio', label: '工房', icon: '🛠' },
  { id: 'settings', label: '設定', icon: '⚙' },
];

/** matchMedia 購読フック（jsdom/SSR では false に倒れる）。 */
function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (cb: () => void) => {
      try {
        const m = window.matchMedia(query);
        m.addEventListener('change', cb);
        return () => m.removeEventListener('change', cb);
      } catch {
        return () => {};
      }
    },
    [query],
  );
  const getSnapshot = useCallback(() => {
    try {
      return window.matchMedia(query).matches;
    } catch {
      return false;
    }
  }, [query]);
  return useSyncExternalStore(subscribe, getSnapshot);
}

export function App() {
  const api = useStudioApi();
  const revision = useStudioStore((s) => s.revision);
  const players = useStudioStore((s) => s.ruleSet.players);
  // boot: skipTitle なら直接 play（SoloMahjong の mount effect が即配牌する）
  const [screen, setScreen] = useState<Screen>(() => (getSettings().display.skipTitle ? 'play' : 'title'));
  // play は初回遷移で mount し、以後は隠すだけ（§5.1 必須要件）
  const [playMounted, setPlayMounted] = useState<boolean>(() => getSettings().display.skipTitle);
  const [studioTab, setStudioTab] = useState<StudioTab>('rules');
  // 未保存ドット: 最後に保存(または boot hydrate)した時点の revision との差分で判定
  const [savedRevision, setSavedRevision] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);
  const reduced = useEffectiveReduced();
  const isNarrow = useMediaQuery('(max-width: 767px)');
  // Online WaitingRoom is a portrait card UI rendered by SoloMahjong; the force-landscape rotation is
  // for the BOARD only, so suppress it while the online table is in 'waiting' (else the lobby is sideways).
  const onlinePhase = useOnlinePhase();
  useConfigSaveListener();

  useEffect(() => {
    if (screen === 'play') setPlayMounted(true);
  }, [screen]);

  // Single nav entry point for the top-bar / logo. When an online table is live, leave it first (confirm
  // only if a match is in progress) via exitOnline; otherwise just switch screens.
  const goTo = useCallback((to: ExitTo) => {
    if (getOnlineTable()) {
      exitOnline(to, (m) => window.confirm(m)); // exitOnline dispatches mj:navigate on commit
    } else {
      setScreen(to);
    }
  }, []);
  // Main-nav (対局/工房/設定) click. 対局 while online = return to the SAME board (no teardown); 工房/設定
  // while online = leave via the guarded teardown. Offline = plain screen switch.
  const onNav = useCallback((to: Screen) => {
    if (to === 'play' || !getOnlineTable()) setScreen(to);
    else goTo(to as ExitTo);
  }, [goTo]);

  // Boot-time restore: hydrate the persisted working ruleset/catalog so saved edits survive a reload.
  // hydrate も revision を bump するので、完了後の revision を「保存済み」基準点にする。
  useEffect(() => {
    void loadConfig().then((c) => {
      if (c.ruleSet || c.catalog) api.getState().hydrate(c);
      setSavedRevision(api.getState().revision);
    });
  }, [api]);

  // 設定 → 音量 hydrate（sound.ts は永続化を持たない: D10。以後の変更は SettingsPanel が即時反映）
  useEffect(() => {
    const s = getSettings();
    sfx.setVolume(s.audio.muted ? 0 : s.audio.masterVolume / 100);
  }, []);

  // integrate seam: SoloMahjong の HUD ☰ メニュー等からの画面遷移要求 (+ exitOnline 後の遷移先指定)。
  useEffect(() => {
    const onNav = (e: Event) => {
      const d: unknown = (e as CustomEvent).detail;
      if (d === 'title' || d === 'play' || d === 'online' || d === 'studio' || d === 'settings') setScreen(d);
    };
    window.addEventListener('mj:navigate', onNav);
    return () => window.removeEventListener('mj:navigate', onNav);
  }, []);

  // トーストは 1 枚だけ・3 秒で自動消滅（§5.1）
  const showToast = (text: string) => {
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    setToast(text);
    toastTimer.current = window.setTimeout(() => {
      setToast(null);
      toastTimer.current = null;
    }, 3000);
  };
  useEffect(
    () => () => {
      if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  const onSave = async () => {
    try {
      // Persist BOTH the working ruleset and the catalog: a custom (DSL) yaku lives in the catalog,
      // so saving only the ruleset would drop it on reload (the boot loader restores both).
      const st = api.getState();
      const r = await saveRuleSet('working', st.ruleSet);
      const c = r.ok ? await saveCatalog(st.catalog) : { ok: false as const, error: 'skipped' };
      if (r.ok && c.ok) {
        api.getState().markSaved();
        setSavedRevision(api.getState().revision);
        showToast('保存しました ✓');
      } else {
        showToast(`保存に失敗しました: ${(r.ok ? c.error : r.error) ?? 'unknown'}`);
      }
    } catch (e) {
      showToast(`保存エラー: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const unsaved = revision !== savedRevision;
  const showTopBar = !isNarrow && screen !== 'title';
  const showTabBar = isNarrow && (screen === 'studio' || screen === 'settings'); // play は HUD ☰ / title は画面内リンク

  return (
    <div
      data-motion={reduced ? 'reduced' : 'full'}
      style={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: 'var(--font-ui, system-ui, sans-serif)',
        background: 'var(--felt-bg, #1a5a40)',
      }}
    >
      {showTopBar && (
        <header
          style={{
            flex: '0 0 auto',
            height: 48,
            display: 'flex',
            alignItems: 'center',
            padding: '0 12px',
            gap: 12,
            background: 'var(--surface-panel, rgba(13,27,21,0.86))',
            borderBottom: '1px solid var(--border-panel, rgba(255,255,255,0.10))',
            zIndex: 30, // --z-hud 相当（バーは盤面より手前・モーダルより奥）
          }}
        >
          <button
            type="button"
            onClick={() => goTo('title')}
            aria-label="タイトルへ"
            style={{
              fontFamily: 'var(--font-display, serif)',
              fontSize: 24,
              lineHeight: 1,
              color: 'var(--gold-400, #e8c14a)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              minWidth: 44,
              minHeight: 44,
            }}
          >
            雀
          </button>
          <nav style={{ display: 'flex', gap: 8, margin: '0 auto' }}>
            {NAV.map((n) => {
              const active = screen === n.id;
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => onNav(n.id)}
                  aria-current={active ? 'page' : undefined}
                  style={{
                    minHeight: 36,
                    padding: '6px 18px',
                    borderRadius: 'var(--r-full, 999px)',
                    border: active
                      ? '1px solid var(--border-gold, rgba(212,175,55,0.4))'
                      : '1px solid transparent',
                    background: active ? 'var(--surface-card, rgba(16,32,25,0.92))' : 'transparent',
                    color: active ? 'var(--gold-300, #f4d97b)' : 'var(--text-mid, rgba(255,251,235,0.72))',
                    fontWeight: active ? 700 : 400,
                    fontSize: 13,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {n.label}
                </button>
              );
            })}
          </nav>
          {/* ロゴ分の右バランス取り（中央ナビを光学センターに保つ） */}
          <span style={{ width: 44, flex: '0 0 auto' }} aria-hidden />
        </header>
      )}

      <main style={{ flex: '1 1 auto', minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
        {screen === 'title' && (
          // 雀魂参考のホーム (flow.md メイン画面 l.105-110). fit-to-viewport で自前に高さ管理するため
          // スクロールラッパは付けない。段位戦/福引/イベント/役解説 は未実装モードなので準備中トースト。
          // ソロ→play(即配牌・対局保持), 友人戦→online, 工房/設定→各画面. 立ち絵は art.ref で後差し(実アセット)。
          <HomeScreen
            onRanked={() => showToast('段位戦は準備中です')}
            onFriend={() => setScreen('online')}
            onSolo={() => setScreen('play')}
            onFukubiki={() => showToast('福引は準備中です')}
            onEvents={() => showToast('イベントは準備中です')}
            onYakuGuide={() => showToast('役解説は準備中です')}
            onStudio={() => setScreen('studio')}
            onSettings={() => setScreen('settings')}
          />
        )}

        {screen === 'online' && (
          <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <OnlineLobby onEnterGame={() => setScreen('play')} onBack={() => setScreen('title')} />
          </div>
        )}

        {/* play: mount したまま display で隠す（対局状態を画面遷移で失わない）。
            overflow:hidden で外側スクロールを禁止（対局は内部で fit 自己完結・P2 不変条件）。
            OrientationGate が play 表示中の portrait のみ対局を CSS で 90°回転 (横長キャンバス化)。
            回転ラッパは position:fixed で viewport 基準なので親の高さに依存しない (この div が
            display:none だと子も消えるため display:flex は維持)。横向きでは素のまま描画。 */}
        {playMounted && (
          <div
            style={{
              display: screen === 'play' ? 'flex' : 'none',
              flexDirection: 'column',
              flex: '1 1 auto',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <OrientationGate active={screen === 'play' && onlinePhase !== 'waiting'}>
              <SoloMahjong />
            </OrientationGate>
          </div>
        )}

        {screen === 'studio' && (
          <div
            style={{
              flex: '1 1 auto',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--surface-ivory, #fbf7ea)',
              color: '#1d2b1d',
            }}
          >
            {/* 工房ヘッダ（sticky 相当: スクロールは下の main 側のみ）。Save config はここだけ (§5.1) */}
            <header
              style={{
                flex: '0 0 auto',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '8px 12px 6px',
                flexWrap: 'wrap',
              }}
            >
              <h1 style={{ fontSize: 16, margin: 0 }}>Mahjong Engine Studio</h1>
              <span style={{ fontSize: 12, color: '#777' }}>
                {players}p · rev {revision}
              </span>
              <span style={{ marginLeft: 'auto', position: 'relative', display: 'inline-block' }}>
                <button type="button" onClick={() => void onSave()} style={{ padding: '8px 14px', cursor: 'pointer' }}>
                  Save config
                </button>
                {unsaved && (
                  <span
                    aria-label="未保存の変更あり"
                    title="未保存の変更あり"
                    style={{
                      position: 'absolute',
                      top: -3,
                      right: -3,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'var(--c-danger, #e5484d)',
                    }}
                  />
                )}
              </span>
            </header>
            {isNarrow ? (
              // <768px: 横スクロールチップ列（高さ 40px、chip 間 8px、scroll-snap）
              <nav
                style={{
                  flex: '0 0 auto',
                  display: 'flex',
                  gap: 8,
                  overflowX: 'auto',
                  padding: '0 12px 8px',
                  scrollSnapType: 'x proximity',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                {STUDIO_TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setStudioTab(t.id)}
                    style={{
                      flex: '0 0 auto',
                      height: 40,
                      padding: '0 14px',
                      borderRadius: 'var(--r-full, 999px)',
                      border: `1px solid ${studioTab === t.id ? '#2b6e2b' : '#cfc9b4'}`,
                      background: studioTab === t.id ? '#2b6e2b' : '#fff',
                      color: studioTab === t.id ? '#fff' : '#1d2b1d',
                      fontWeight: studioTab === t.id ? 700 : 400,
                      fontSize: 13,
                      whiteSpace: 'nowrap',
                      scrollSnapAlign: 'start',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </nav>
            ) : (
              // ≥768px: 現行踏襲の下線タブ
              <nav
                style={{
                  flex: '0 0 auto',
                  display: 'flex',
                  gap: 4,
                  borderBottom: '1px solid #ccc',
                  padding: '0 12px',
                  flexWrap: 'wrap',
                }}
              >
                {STUDIO_TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setStudioTab(t.id)}
                    style={{
                      padding: '6px 12px',
                      border: 'none',
                      borderBottom: studioTab === t.id ? '2px solid #2b6e2b' : '2px solid transparent',
                      background: 'transparent',
                      fontWeight: studioTab === t.id ? 700 : 400,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: 13,
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </nav>
            )}
            <main style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto', padding: '8px 12px 12px' }}>
              {studioTab === 'rules' && <RuleSetEditor />}
              {studioTab === 'yaku' && <YakuToggle />}
              {studioTab === 'addYaku' && <AddYaku />}
              {studioTab === 'scoreTable' && <ScoreTableInspector />}
              {studioTab === 'example' && <ExampleHand />}
              {studioTab === 'play' && <PlaySandbox />}
            </main>
          </div>
        )}

        {screen === 'settings' && (
          <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <SettingsPanel />
          </div>
        )}
      </main>

      {showTabBar && (
        <nav
          aria-label="メインナビゲーション"
          style={{
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'stretch',
            height: 'calc(56px + env(safe-area-inset-bottom))',
            paddingBottom: 'env(safe-area-inset-bottom)',
            background: 'var(--surface-panel, rgba(13,27,21,0.86))',
            borderTop: '1px solid var(--border-panel, rgba(255,255,255,0.10))',
          }}
        >
          {NAV.map((n) => {
            const active = screen === n.id;
            return (
              <button
                key={n.id}
                type="button"
                onClick={() => onNav(n.id)}
                aria-current={active ? 'page' : undefined}
                style={{
                  flex: '1 1 0',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: active ? 'var(--gold-300, #f4d97b)' : 'var(--text-mid, rgba(255,251,235,0.72))',
                  fontFamily: 'inherit',
                }}
              >
                <span style={{ fontSize: 24, lineHeight: 1 }} aria-hidden>
                  {n.icon}
                </span>
                <span style={{ fontSize: 10, fontWeight: active ? 700 : 400 }}>{n.label}</span>
              </button>
            );
          })}
        </nav>
      )}

      {toast && (
        // theme.css .toast: fixed 中央下 + var(--z-toast)。タブバー表示中はその上に逃がす
        <div
          className="toast"
          role="status"
          aria-live="polite"
          style={showTabBar ? { bottom: 'calc(68px + env(safe-area-inset-bottom))' } : undefined}
        >
          {toast}
        </div>
      )}
    </div>
  );
}
